import React, { useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { useTranslation } from 'react-i18next';
import { useUser } from '../WebSocketContext';

function OrganizationIdentityDetails({ organizationId, organizationName }) {
  const { t } = useTranslation();
  const idRef = useRef(null);
  const [copyStatus, setCopyStatus] = useState('');

  const copyOrganizationId = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard is unavailable');
      }
      await navigator.clipboard.writeText(organizationId);
      setCopyStatus('copied');
    } catch {
      setCopyStatus('copy_failed');
      if (idRef.current) {
        idRef.current.focus();
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(idRef.current);
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  };

  return (
    <Box sx={{ mt: 1, color: 'text.secondary' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'center', columnGap: 1, rowGap: 0.25 }}>
        {organizationName && (
          <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
            {t('global_setting.organization_identity.active_organization', { name: organizationName })}
          </Typography>
        )}
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {organizationName && <Box component="span" aria-hidden="true" sx={{ mr: 0.5, display: { xs: 'none', sm: 'inline' } }}>·</Box>}
          <Typography component="span" variant="caption">
            <Box component="span" sx={visuallyHidden}>{t('global_setting.organization_identity.id')} : </Box>
            <Box component="span" aria-hidden="true">{t('global_setting.organization_identity.id_short')} : </Box>
            <Box component="span" ref={idRef} tabIndex={-1} sx={{ userSelect: 'all' }}>{organizationId}</Box>
          </Typography>
          <Tooltip title={t(`global_setting.organization_identity.${copyStatus === 'copied' ? 'copied' : 'copy'}`)}>
            <IconButton
              size="small"
              aria-label={t('global_setting.organization_identity.copy')}
              onClick={copyOrganizationId}
              sx={{ width: 32, height: 32, color: copyStatus === 'copied' ? 'success.main' : 'text.secondary' }}
            >
              {copyStatus === 'copied' ? <CheckRoundedIcon sx={{ fontSize: 16 }} /> : <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Typography
        role="status"
        aria-live="polite"
        variant="caption"
        sx={copyStatus === 'copy_failed' ? { display: 'block', mt: 0.5, color: 'error.main' } : visuallyHidden}
      >
        {copyStatus ? t(`global_setting.organization_identity.${copyStatus}`) : ''}
      </Typography>
    </Box>
  );
}

export default function OrganizationIdentity() {
  const { organizationId, organizations = [] } = useUser();
  if (!organizationId) return null;

  const organization = organizations.find((item) => String(item.id) === String(organizationId));
  return (
    <OrganizationIdentityDetails
      key={String(organizationId)}
      organizationId={String(organizationId)}
      organizationName={organization?.name}
    />
  );
}
